import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import styles from './AnnuaireTrackingDialog.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { AnnuaireVisitResult } from '@/modules/API/software/annuaireTracking'
import { SET_DATA_SHARING_CONSENT } from '@/modules/API/account/accountHandler'
import { Role } from '@/types/Role.ts'

interface AnnuaireTrackingDialogProps {
    open: boolean
    result: AnnuaireVisitResult
    handleClose: () => void
    role: Role
    onConsentAccepted?: () => void
}

const AnnuaireTrackingDialog: FunctionComponent<AnnuaireTrackingDialogProps> = ({
    open,
    result,
    handleClose,
    role,
    onConsentAccepted
}: AnnuaireTrackingDialogProps) => {
    const { t } = useTranslation()
    const [consentAccepted, setConsentAccepted] = useState<boolean>(false)

    const [setDataSharingConsent] = useMutation(SET_DATA_SHARING_CONSENT)

    function handleConsentChange(value: boolean): void {
        setConsentAccepted(value)
        if (value) {
            setDataSharingConsent({ variables: { consent: true } })
            onConsentAccepted?.()
        }
    }

    const showTransferred: boolean = result === 'transferred' || (result === 'consent_needed' && consentAccepted)

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('annuaireTracking')} parent={role === Role.parent} />
            <DialogBody>
                <div className={styles.DialogBody}>
                    {showTransferred && (
                        <div className={styles.TextContainer}>
                            <p className={styles.Text}>{t('annuaireTransferred')}</p>
                        </div>
                    )}
                    {result === 'consent_needed' && !consentAccepted && (
                        <>
                            <div className={styles.TextContainer}>
                                <p className={styles.Text}>{t('annuaireConsentNeeded')}</p>
                            </div>
                            <div className={styles.CheckBoxContainer}>
                                <Checkbox
                                    text={t('dataSharingConsent')}
                                    checked={consentAccepted}
                                    onChange={handleConsentChange}
                                    parent={role === Role.parent}
                                />
                            </div>
                        </>
                    )}
                    {result === 'has_structure' && (
                        <div className={styles.TextContainer}>
                            <p className={styles.Text}>{t('annuaireHasStructure')}</p>
                        </div>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type='close'
                            onClick={handleClose}
                            parent={role === Role.parent}
                            className={styles.Button}
                        />
                    </div>
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AnnuaireTrackingDialog
